// PhutballEngine.js
// Client-side deterministic game engine for P2P multiplayer.
// Ported from phutball_env.py - same rules, same board, same outcomes.

import { TILE_TYPE, renderLabeledBoardLines } from './utils';

const { EMPTY, PHUTBALL, PHUTBALL_MAN, END_HI, END_LO } = TILE_TYPE;

export default class PhutballEngine {
    constructor(rows = 21, cols = 15) {
        if (rows % 2 === 0 || cols % 2 === 0) {
            throw new Error('Board dimensions must be odd numbers.');
        }
        this.rows = rows;
        this.cols = cols;
        this.totalPositions = rows * cols;
        this.maxPosVisits = 5;

        this.board = [];
        this.currentPlayer = 1;
        this.phutballPos = [0, 0];
        this.winner = 0;
        this.numTurns = 0;
        this.jumpSeq = [];
        this.jumpVisitCounts = {};
        this.moveHistory = [];

        this.reset();
    }

    // ── Board Setup ──────────────────────────────────────────────────

    reset() {
        this.board = [];
        for (let r = 0; r < this.rows; r++) {
            const row = new Array(this.cols);
            if (r === 0) {
                row.fill(END_HI);
            } else if (r === this.rows - 1) {
                row.fill(END_LO);
            } else {
                row.fill(EMPTY);
            }
            this.board.push(row);
        }

        const centerRow = Math.floor(this.rows / 2);
        const centerCol = Math.floor(this.cols / 2);
        this.phutballPos = [centerRow, centerCol];
        this.board[centerRow][centerCol] = PHUTBALL;

        this.currentPlayer = 1;
        this.numTurns = 0;
        this.winner = 0;
        this.jumpSeq = [];
        this.jumpVisitCounts = {};
        this.moveHistory = [];
    }

    // ── State Snapshots ──────────────────────────────────────────────

    getSnapshot() {
        return {
            board: this.board.map(row => [...row]),
            currentPlayer: this.currentPlayer,
            phutballPos: [...this.phutballPos],
            numTurns: this.numTurns,
            winner: this.winner,
            jumpSeq: this.jumpSeq.map(pos => [...pos]),
            jumpVisitCounts: { ...this.jumpVisitCounts },
        };
    }

    loadSnapshot(snapshot) {
        this.board = snapshot.board.map(row => [...row]);
        this.currentPlayer = snapshot.currentPlayer;
        this.phutballPos = [...snapshot.phutballPos];
        this.numTurns = snapshot.numTurns;
        this.winner = snapshot.winner;
        this.jumpSeq = (snapshot.jumpSeq || []).map(pos => [...pos]);
        this.jumpVisitCounts = { ...snapshot.jumpVisitCounts };
    }

    getState() {
        return {
            board: this.board,
            currentPlayer: this.currentPlayer,
            phutballPos: this.phutballPos,
            winner: this.winner,
            numTurns: this.numTurns,
            isJumping: this.isJumpSeqUnderway(),
            jumpSeq: this.jumpSeq,
        };
    }

    // ── Win Detection ────────────────────────────────────────────────

    checkForWin() {
        // Player 1 wins by getting ball to row <= 1 (top goal)
        // Player 2 wins by getting ball to row >= rows-2 (bottom goal)
        if (this.phutballPos[0] <= 1) return 1;
        if (this.phutballPos[0] >= this.rows - 2) return 2;
        return 0;
    }

    // ── Turn Management ──────────────────────────────────────────────

    isJumpSeqUnderway() {
        return this.jumpSeq.length > 0;
    }

    endJumpSeq() {
        this.jumpSeq = [];
        this.jumpVisitCounts = {};
        this.winner = this.checkForWin();
        return this.winner !== 0;
    }

    endTurn() {
        this.currentPlayer = 3 - this.currentPlayer;
        this.numTurns += 1;
    }

    // ── Validation ───────────────────────────────────────────────────

    isLegalPlacement(pos) {
        const [r, c] = pos;
        // Must be within playable rows (1 to rows-2 inclusive)
        // Rows 0 and rows-1 are deep endzones - forbidden for placement
        if (r <= 0 || r >= this.rows - 1) return false;
        if (c < 0 || c >= this.cols) return false;
        const tile = this.board[r][c];
        return tile === EMPTY;
    }

    checkSingleJump(startPos, endPos, board) {
        board = board || this.board;
        const [r0, c0] = startPos;
        const [r1, c1] = endPos;

        // Landing must be in bounds
        if (r1 < 0 || r1 >= this.rows || c1 < 0 || c1 >= this.cols) return false;
        // Can't jump to same position
        if (r0 === r1 && c0 === c1) return false;

        const dr = r1 - r0;
        const dc = c1 - c0;

        // Must be straight line
        if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) return false;

        const rStep = Math.sign(dr);
        const cStep = Math.sign(dc);
        const steps = Math.max(Math.abs(dr), Math.abs(dc));

        // Must jump over at least one cell (distance >= 2)
        if (steps < 2) return false;

        // All intermediate cells must contain men
        for (let i = 1; i < steps; i++) {
            const cr = r0 + i * rStep;
            const cc = c0 + i * cStep;
            if (cr < 0 || cr >= this.rows || cc < 0 || cc >= this.cols) return false;
            if (board[cr][cc] !== PHUTBALL_MAN) return false;
        }

        // Landing must be empty or endzone (not man, not ball)
        const landingTile = board[r1][c1];
        if (landingTile === PHUTBALL_MAN || landingTile === PHUTBALL) return false;

        return true;
    }

    calculateJumpedMen(startPos, endPos) {
        const [r0, c0] = startPos;
        const [r1, c1] = endPos;
        const dr = r1 - r0;
        const dc = c1 - c0;

        // Must be straight line
        if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) return [];

        const rStep = Math.sign(dr);
        const cStep = Math.sign(dc);
        const steps = Math.max(Math.abs(dr), Math.abs(dc));
        const jumped = [];

        for (let i = 1; i < steps; i++) {
            const cr = r0 + i * rStep;
            const cc = c0 + i * cStep;
            if (cr >= 0 && cr < this.rows && cc >= 0 && cc < this.cols) {
                if (this.board[cr][cc] === PHUTBALL_MAN) {
                    jumped.push([cr, cc]);
                }
            }
        }
        return jumped;
    }

    getLegalJumps() {
        const [startR, startC] = this.phutballPos;
        const legal = [];
        const directions = [
            [-1, -1], [-1, 0], [-1, 1],
            [0, -1],           [0, 1],
            [1, -1],  [1, 0],  [1, 1],
        ];
        const maxDim = Math.max(this.rows, this.cols);

        for (const [dr, dc] of directions) {
            let menJumped = 0;
            for (let i = 1; i < maxDim; i++) {
                const cr = startR + i * dr;
                const cc = startC + i * dc;

                if (cr < 0 || cr >= this.rows || cc < 0 || cc >= this.cols) break;

                const tile = this.board[cr][cc];
                if (tile === PHUTBALL_MAN) {
                    menJumped++;
                    continue;
                } else if (tile === EMPTY || tile === END_HI || tile === END_LO) {
                    if (menJumped > 0) {
                        legal.push([cr, cc]);
                    }
                    break;
                } else {
                    // Ball or something else - stop
                    break;
                }
            }
        }
        return legal;
    }

    getLegalPlacements() {
        const legal = [];
        for (let r = 1; r < this.rows - 1; r++) {
            for (let c = 0; c < this.cols; c++) {
                if (this.board[r][c] === EMPTY) {
                    legal.push([r, c]);
                }
            }
        }
        return legal;
    }

    // ── Move Application ─────────────────────────────────────────────

    applyPlacement(pos) {
        const [r, c] = pos;
        if (!this.isLegalPlacement(pos)) {
            return { success: false, reason: 'Illegal placement' };
        }
        if (this.isJumpSeqUnderway()) {
            return { success: false, reason: 'Cannot place during jump sequence' };
        }

        this.board[r][c] = PHUTBALL_MAN;
        this.endTurn();
        return { success: true };
    }

    applyJump(targetPos) {
        const [r1, c1] = targetPos;

        if (!this.checkSingleJump(this.phutballPos, targetPos)) {
            return { success: false, reason: 'Illegal jump' };
        }

        // Initialize jump sequence if not already started
        if (!this.isJumpSeqUnderway()) {
            const startPos = [...this.phutballPos];
            this.jumpSeq = [startPos];
            const startKey = `${startPos[0]},${startPos[1]}`;
            this.jumpVisitCounts = { [startKey]: 1 };
        }

        // Track visit count
        const targetKey = `${r1},${c1}`;
        this.jumpVisitCounts[targetKey] = (this.jumpVisitCounts[targetKey] || 0) + 1;
        this.jumpSeq.push([r1, c1]);

        // Update board: clear old ball pos, remove jumped men, place ball at target
        const [r0, c0] = this.phutballPos;
        const jumpedMen = this.calculateJumpedMen(this.phutballPos, targetPos);

        this.board[r0][c0] = EMPTY;
        for (const [jr, jc] of jumpedMen) {
            this.board[jr][jc] = EMPTY;
        }
        this.phutballPos = [r1, c1];
        this.board[r1][c1] = PHUTBALL;

        // Note: do NOT auto-terminate on endzone landing here. Per the
        // documented rule (LLM tutorial §6b), only the FINAL landing of a
        // turn determines win/loss — mid-sequence endzone passes are legal
        // and necessary for passthrough puzzles. applyHalt → endJumpSeq
        // checks the row-based winner once the player commits to stopping.
        return { success: true, terminated: false };
    }

    applyHalt() {
        if (!this.isJumpSeqUnderway()) {
            return { success: false, reason: 'No jump sequence to halt' };
        }

        const terminated = this.endJumpSeq();
        if (!terminated) {
            this.endTurn();
        }
        return { success: true, terminated, winner: this.winner };
    }

    // ── High-Level Move Interface ────────────────────────────────────
    // move: { type: 'placement'|'jump', path: [[r,c], ...] }
    //   placement: path has one entry (the placement position)
    //   jump: path has one or more entries (jump landing positions), halt is implicit

    applyMove(move) {
        if (this.winner !== 0) {
            return { success: false, reason: 'Game is already over' };
        }

        const { type, path } = move;

        if (type === 'placement') {
            if (!path || path.length !== 1) {
                return { success: false, reason: 'Placement requires exactly one position' };
            }
            const result = this.applyPlacement(path[0]);
            if (result.success) {
                this.moveHistory.push({ ...move, player: 3 - this.currentPlayer });
            }
            return result;
        }

        if (type === 'jump') {
            if (!path || path.length === 0) {
                return { success: false, reason: 'Jump requires at least one position' };
            }

            const player = this.currentPlayer;

            // Apply each jump in the sequence
            for (let i = 0; i < path.length; i++) {
                const result = this.applyJump(path[i]);
                if (!result.success) {
                    return { success: false, reason: `Jump ${i + 1} failed: ${result.reason}` };
                }
                if (result.terminated) {
                    this.moveHistory.push({ type, path, player });
                    return { success: true, terminated: true, winner: result.winner };
                }
            }

            // Implicit halt after all jumps
            const haltResult = this.applyHalt();
            this.moveHistory.push({ type, path, player });
            return haltResult;
        }

        return { success: false, reason: `Unknown move type: ${type}` };
    }

    // ── State Hash for Reconciliation ────────────────────────────────

    computeStateHash() {
        // Deterministic string representation of game state
        const parts = [];
        for (let r = 0; r < this.rows; r++) {
            for (let c = 0; c < this.cols; c++) {
                parts.push(this.board[r][c]);
            }
        }
        parts.push('|', this.currentPlayer, '|', this.phutballPos[0], ',', this.phutballPos[1]);
        parts.push('|', this.winner);

        const str = parts.join(',');
        // Simple hash (djb2) - sufficient for state comparison
        let hash = 5381;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
        }
        return hash.toString(16);
    }

    // ── Replay for Reconnection ──────────────────────────────────────

    replayMoves(moves) {
        this.reset();
        for (let i = 0; i < moves.length; i++) {
            const result = this.applyMove(moves[i]);
            if (!result.success) {
                return { success: false, failedAt: i, reason: result.reason };
            }
        }
        return { success: true };
    }

    getMoveHistory() {
        return this.moveHistory.map(m => ({ ...m }));
    }

    getMoveCount() {
        return this.moveHistory.length;
    }

    // ── Text Rendering (labeled absolute matrix coordinates) ────────

    textRender(_forPlayer = null) {
        const TILE_CHARS = { [EMPTY]: '┼', [PHUTBALL]: 'O', [PHUTBALL_MAN]: 'x', [END_HI]: '+', [END_LO]: '-' };

        const grid = [];
        for (let r = 0; r < this.rows; r++) {
            let row = '';
            for (let c = 0; c < this.cols; c++) {
                row += TILE_CHARS[this.board[r][c]] ?? '?';
            }
            grid.push(row);
        }

        const labeled = renderLabeledBoardLines(grid, this.cols);

        const header = `TURN: ${this.numTurns + 1} PLAYER: ${this.currentPlayer}`;
        const lines = ['<board_state>', header];
        if (this.isJumpSeqUnderway()) {
            lines.push(`JUMP SEQ ACTIVE (Step ${this.jumpSeq.length - 1})`);
        }
        lines.push(...labeled, '</board_state>');
        if (this.winner > 0) {
            lines.push(`Winner: ${this.winner}`);
        }
        return lines.join('\n');
    }
}
